import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { canAccess } from '../../helper/permissions';
import { renderMenuIcon } from '../../helper/menuIcons';
import {
    FaBars,
    FaEdit,
    FaGripVertical,
    FaPlusCircle,
    FaSave,
    FaTimes,
    FaTrashAlt,
} from 'react-icons/fa';

const emptyForm = {
    menu_id: null,
    menu_key: '',
    name: '',
    path: '',
    icon: 'FaCircle',
    sort_order: 99,
    parent_id: '',
    is_active: true,
};

const fieldClass =
    'block w-full rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/25 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-100';

const labelClass = 'mb-0.5 block text-[11px] font-semibold text-gray-600 dark:text-gray-300';

const cloneTree = (nodes) =>
    (nodes || []).map((n) => ({
        ...n,
        children: cloneTree(n.children || []),
    }));

const flattenTree = (nodes, depth = 0, parentId = null) => {
    const rows = [];
    (nodes || []).forEach((n) => {
        rows.push({
            ...n,
            depth,
            parent_id: parentId,
            hasChildren: !!(n.children || []).length,
        });
        rows.push(...flattenTree(n.children || [], depth + 1, n.menu_id));
    });
    return rows;
};

const treeToPayload = (nodes, parentId = null) => {
    const items = [];
    (nodes || []).forEach((n, i) => {
        items.push({
            menu_id: n.menu_id,
            parent_id: parentId,
            sort_order: i + 1,
        });
        items.push(...treeToPayload(n.children || [], n.menu_id));
    });
    return items;
};

const collectDescendantIds = (node, set = new Set()) => {
    (node.children || []).forEach((c) => {
        set.add(c.menu_id);
        collectDescendantIds(c, set);
    });
    return set;
};

/** Remove node by id from tree; returns { node, tree } */
const takeNode = (tree, menuId) => {
    const next = cloneTree(tree);
    const walk = (nodes) => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].menu_id === menuId) {
                const [node] = nodes.splice(i, 1);
                return node;
            }
            const found = walk(nodes[i].children || []);
            if (found) return found;
        }
        return null;
    };
    return { node: walk(next), tree: next };
};

/**
 * position: before | after | inside
 * supports unlimited nesting (sub → sub)
 */
const placeNode = (tree, node, targetId, position) => {
    const banned = collectDescendantIds(node);
    banned.add(node.menu_id);
    if (banned.has(targetId)) return null;

    const moving = {
        ...node,
        children: cloneTree(node.children || []),
    };

    const next = cloneTree(tree);

    const insertSibling = (list, targetMenuId, pos) => {
        const idx = list.findIndex((x) => x.menu_id === targetMenuId);
        if (idx < 0) return false;
        list.splice(pos === 'before' ? idx : idx + 1, 0, moving);
        return true;
    };

    const walk = (nodes) => {
        if (position === 'inside') {
            for (const n of nodes) {
                if (n.menu_id === targetId) {
                    n.children = [...(n.children || []), moving];
                    return true;
                }
            }
        } else if (insertSibling(nodes, targetId, position)) {
            return true;
        }

        for (const n of nodes) {
            if (walk(n.children || [])) return true;
        }
        return false;
    };

    if (!walk(next)) return null;
    return next;
};

const MenuManage = ({ data }) => {
    const header = { headers: { Authorization: `Bearer ${data.token}` }, withCredentials: true };
    const permissions = data?.user?.permissions || {};
    const canCreate = canAccess(permissions, 'menu_manage', 'create');
    const canEdit = canAccess(permissions, 'menu_manage', 'edit');
    const canDelete = canAccess(permissions, 'menu_manage', 'delete');

    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [dragId, setDragId] = useState(null);
    const [dropHint, setDropHint] = useState(null); // { id, position }
    const dragIdRef = useRef(null);

    const flatRows = useMemo(() => flattenTree(tree), [tree]);

    const parentOptions = useMemo(() => {
        const banned = new Set();
        if (form.menu_id) {
            const self = flatRows.find((r) => r.menu_id === form.menu_id);
            banned.add(form.menu_id);
            if (self) {
                // ban descendants when editing
                const addKids = (id) => {
                    flatRows
                        .filter((r) => r.parent_id === id)
                        .forEach((r) => {
                            banned.add(r.menu_id);
                            addKids(r.menu_id);
                        });
                };
                addKids(form.menu_id);
            }
        }
        return flatRows.filter((r) => !banned.has(r.menu_id));
    }, [flatRows, form.menu_id]);

    const loadMenus = async () => {
        setLoading(true);
        try {
            const res = await axios.get('api/menu-access/menus', header);
            setTree(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error(e);
            Swal.fire('Gagal', 'Tidak bisa memuat daftar menu', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMenus();
        // eslint-disable-next-line
    }, []);

    const persistOrder = async (nextTree) => {
        setReordering(true);
        const prev = tree;
        setTree(nextTree);
        try {
            const res = await axios.put(
                'api/menu-access/menus/reorder',
                { items: treeToPayload(nextTree) },
                header
            );
            setTree(Array.isArray(res.data) ? res.data : nextTree);
            window.dispatchEvent(new Event('auth:refresh'));
        } catch (err) {
            setTree(prev);
            Swal.fire(
                'Gagal',
                err?.response?.data?.message || 'Gagal mengubah urutan menu',
                'error'
            );
        } finally {
            setReordering(false);
        }
    };

    const resolveDropPosition = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const y = e.clientY - rect.top;
        const ratio = y / rect.height;
        // atas = before, tengah = inside (jadi submenu), bawah = after
        if (ratio < 0.28) return 'before';
        if (ratio > 0.72) return 'after';
        return 'inside';
    };

    const onDragStart = (e, row) => {
        if (!canEdit) return;
        dragIdRef.current = row.menu_id;
        setDragId(row.menu_id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(row.menu_id));
    };

    const onDragOver = (e, row) => {
        if (!canEdit || !dragIdRef.current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragIdRef.current === row.menu_id) {
            setDropHint(null);
            return;
        }
        const position = resolveDropPosition(e);
        setDropHint({ id: row.menu_id, position });
    };

    const onDrop = async (e, row) => {
        e.preventDefault();
        if (!canEdit) return;
        const fromId = Number(e.dataTransfer.getData('text/plain') || dragIdRef.current);
        const position = dropHint?.id === row.menu_id ? dropHint.position : resolveDropPosition(e);
        setDropHint(null);
        setDragId(null);
        dragIdRef.current = null;

        if (!fromId || fromId === row.menu_id) return;

        const { node, tree: without } = takeNode(tree, fromId);
        if (!node) return;

        const next = placeNode(without, node, row.menu_id, position);
        if (!next) {
            Swal.fire(
                'Tidak bisa',
                'Tidak bisa drop ke dalam sub-menu milik sendiri.',
                'warning'
            );
            return;
        }
        await persistOrder(next);
    };

    const onDragEnd = () => {
        setDragId(null);
        setDropHint(null);
        dragIdRef.current = null;
    };

    const openCreate = () => {
        setForm({ ...emptyForm, sort_order: (flatRows.length || 0) + 1 });
        setShowForm(true);
    };

    const openEdit = (row) => {
        setForm({
            menu_id: row.menu_id,
            menu_key: row.menu_key || '',
            name: row.name || '',
            path: row.path || '',
            icon: row.icon || 'FaCircle',
            sort_order: row.sort_order ?? 0,
            parent_id: row.parent_id || '',
            is_active: row.is_active !== false,
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setForm(emptyForm);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            Swal.fire('Gagal', 'Nama menu wajib diisi', 'error');
            return;
        }

        const payload = {
            name: form.name.trim(),
            path: form.path.trim(),
            icon: form.icon.trim() || 'FaCircle',
            sort_order: Number(form.sort_order) || 0,
            parent_id: form.parent_id === '' ? null : Number(form.parent_id),
            is_active: !!form.is_active,
        };

        setSaving(true);
        try {
            let res;
            if (form.menu_id) {
                res = await axios.put(`api/menu-access/menus/${form.menu_id}`, payload, header);
            } else {
                res = await axios.post(
                    'api/menu-access/menus',
                    {
                        ...payload,
                        menu_key: form.menu_key.trim() || undefined,
                    },
                    header
                );
            }
            setTree(Array.isArray(res.data) ? res.data : []);
            closeForm();
            if (typeof data?.refreshAuth === 'function') data.refreshAuth();
            window.dispatchEvent(new Event('auth:refresh'));
            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: form.menu_id ? 'Menu diperbarui.' : 'Menu ditambahkan.',
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire(
                'Gagal',
                err?.response?.data?.message || 'Gagal menyimpan menu',
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (row) => {
        Swal.fire({
            title: 'Hapus menu?',
            text: `"${row.name}" akan dihapus permanen.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#5C5E62',
            confirmButtonText: 'Ya, hapus',
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                const res = await axios.delete(`api/menu-access/menus/${row.menu_id}`, header);
                setTree(Array.isArray(res.data) ? res.data : []);
                if (typeof data?.refreshAuth === 'function') data.refreshAuth();
                window.dispatchEvent(new Event('auth:refresh'));
                Swal.fire({
                    icon: 'success',
                    title: 'Terhapus',
                    timer: 1000,
                    showConfirmButton: false,
                });
            } catch (err) {
                Swal.fire(
                    'Gagal',
                    err?.response?.data?.message || 'Gagal menghapus menu',
                    'error'
                );
            }
        });
    };

    const toggleActive = async (row) => {
        if (!canEdit) return;
        try {
            const res = await axios.put(
                `api/menu-access/menus/${row.menu_id}`,
                { is_active: !row.is_active },
                header
            );
            setTree(Array.isArray(res.data) ? res.data : []);
            window.dispatchEvent(new Event('auth:refresh'));
        } catch (err) {
            Swal.fire(
                'Gagal',
                err?.response?.data?.message || 'Gagal mengubah status',
                'error'
            );
        }
    };

    return (
        <>
            <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-100">
                            <FaBars className="text-primary" />
                            Daftar menu sidebar
                        </div>
                        {reordering ? (
                            <span className="text-[11px] font-semibold text-primary">Menyimpan...</span>
                        ) : null}
                    </div>
                    {canCreate ? (
                        <button
                            type="button"
                            onClick={openCreate}
                            className="btn-primary shrink-0"
                        >
                            <FaPlusCircle />
                            Tambah Menu
                        </button>
                    ) : null}
                </div>
                <p className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">
                    Drag ⋮⋮: atas/bawah = urutan, tengah = jadi submenu (bisa nested berkali-kali).
                </p>

                {loading ? (
                    <div className="py-8 text-center text-sm text-gray-500">Memuat...</div>
                ) : (
                    <div className="space-y-0.5 rounded-lg border border-gray-200 p-1 dark:border-gray-700">
                        {flatRows.map((row) => {
                            const hint = dropHint?.id === row.menu_id ? dropHint.position : null;
                            const dragging = dragId === row.menu_id;
                            return (
                                <div
                                    key={row.menu_id}
                                    draggable={canEdit}
                                    onDragStart={(e) => onDragStart(e, row)}
                                    onDragOver={(e) => onDragOver(e, row)}
                                    onDrop={(e) => onDrop(e, row)}
                                    onDragEnd={onDragEnd}
                                    className={`relative flex items-center gap-2 rounded-md px-1.5 py-1.5 text-sm transition ${
                                        dragging
                                            ? 'opacity-40'
                                            : 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700/50'
                                    } ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                    style={{ marginLeft: `${(row.depth || 0) * 18}px` }}
                                >
                                    {hint === 'before' ? (
                                        <div className="absolute inset-x-1 -top-0.5 h-0.5 rounded bg-primary" />
                                    ) : null}
                                    {hint === 'after' ? (
                                        <div className="absolute inset-x-1 -bottom-0.5 h-0.5 rounded bg-primary" />
                                    ) : null}
                                    {hint === 'inside' ? (
                                        <div className="pointer-events-none absolute inset-0 rounded-md ring-2 ring-primary/70" />
                                    ) : null}

                                    <span className="shrink-0 text-gray-300 dark:text-gray-500">
                                        <FaGripVertical className="text-xs" />
                                    </span>
                                    <span className="shrink-0 text-primary">
                                        {renderMenuIcon(row.icon) || <FaBars />}
                                    </span>
                                    <div className="min-w-0 flex-1 text-left">
                                        <p className="truncate text-xs font-bold text-gray-800 dark:text-gray-100">
                                            {row.depth ? `${'—'.repeat(Math.min(row.depth, 3))} ` : ''}
                                            {row.name}
                                            {!row.path ? (
                                                <span className="ml-1 text-[10px] font-semibold text-gray-400">
                                                    group
                                                </span>
                                            ) : null}
                                        </p>
                                        <p className="truncate text-[10px] text-gray-400">
                                            {row.menu_key}
                                            {row.path ? ` · ${row.path}` : ''}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={!canEdit}
                                        onClick={() => toggleActive(row)}
                                        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                            row.is_active
                                                ? 'bg-emerald-50 text-emerald-700'
                                                : 'bg-gray-100 text-gray-500'
                                        }`}
                                    >
                                        {row.is_active ? 'ON' : 'OFF'}
                                    </button>
                                    {canEdit ? (
                                        <button
                                            type="button"
                                            onClick={() => openEdit(row)}
                                            className="rounded p-1.5 text-primary hover:bg-primary/10"
                                        >
                                            <FaEdit className="text-xs" />
                                        </button>
                                    ) : null}
                                    {canDelete ? (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(row)}
                                            className="rounded p-1.5 text-red-500 hover:bg-red-50"
                                        >
                                            <FaTrashAlt className="text-xs" />
                                        </button>
                                    ) : null}
                                </div>
                            );
                        })}
                        {!flatRows.length ? (
                            <div className="py-8 text-center text-sm text-gray-500">Belum ada menu</div>
                        ) : null}
                    </div>
                )}
            </div>

            {showForm ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-3"
                    onClick={closeForm}
                >
                    <form
                        onSubmit={handleSave}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-sm rounded-xl bg-white shadow-2xl dark:bg-gray-800"
                    >
                        <div className="flex items-center justify-between border-b border-gray-100 px-3.5 py-2.5 dark:border-gray-700">
                            <h3 className="text-sm font-bold text-gray-800 dark:text-white">
                                {form.menu_id ? 'Edit Menu' : 'Tambah Menu'}
                            </h3>
                            <button
                                type="button"
                                onClick={closeForm}
                                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                            >
                                <FaTimes className="text-sm" />
                            </button>
                        </div>

                        <div className="space-y-2 px-3.5 py-3">
                            <div className="pill pill-active !px-2.5 !py-2">
                                <span className="text-base leading-none">
                                    {renderMenuIcon(form.icon) || <FaBars />}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-xs font-bold leading-tight">
                                        {form.name.trim() || 'Nama menu'}
                                    </p>
                                    <p className="truncate text-[10px] leading-tight text-white/70">
                                        {form.path.trim() || 'group'}
                                    </p>
                                </div>
                                <label className="flex shrink-0 items-center gap-1 text-[10px] font-semibold">
                                    <input
                                        type="checkbox"
                                        checked={!!form.is_active}
                                        onChange={(e) =>
                                            setForm({ ...form, is_active: e.target.checked })
                                        }
                                        className="h-3 w-3 rounded border-white/40 text-primary"
                                    />
                                    Aktif
                                </label>
                            </div>

                            <div>
                                <label className={labelClass}>Nama *</label>
                                <input
                                    className={fieldClass}
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className={labelClass}>Key</label>
                                    <input
                                        className={`${fieldClass} ${form.menu_id ? 'bg-gray-50 text-gray-500 dark:bg-gray-900/50' : ''}`}
                                        value={form.menu_key}
                                        disabled={!!form.menu_id}
                                        onChange={(e) =>
                                            setForm({ ...form, menu_key: e.target.value })
                                        }
                                        placeholder="auto"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Icon</label>
                                    <input
                                        className={fieldClass}
                                        value={form.icon}
                                        onChange={(e) =>
                                            setForm({ ...form, icon: e.target.value })
                                        }
                                        placeholder="FaCog"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Path</label>
                                <input
                                    className={fieldClass}
                                    value={form.path}
                                    onChange={(e) => setForm({ ...form, path: e.target.value })}
                                    placeholder="/admin/..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className={labelClass}>Parent</label>
                                    <select
                                        className={fieldClass}
                                        value={form.parent_id}
                                        onChange={(e) =>
                                            setForm({ ...form, parent_id: e.target.value })
                                        }
                                    >
                                        <option value="">Root</option>
                                        {parentOptions.map((p) => (
                                            <option key={p.menu_id} value={p.menu_id}>
                                                {'—'.repeat(p.depth || 0)}
                                                {p.depth ? ' ' : ''}
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Urutan</label>
                                    <input
                                        type="number"
                                        className={fieldClass}
                                        value={form.sort_order}
                                        onChange={(e) =>
                                            setForm({ ...form, sort_order: e.target.value })
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2 border-t border-gray-100 px-3.5 py-2.5 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={closeForm}
                                className="flex-1 rounded-lg border border-gray-200 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                            >
                                Batal
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex flex-[1.3] items-center justify-center gap-1.5 btn-primary !min-h-0 !py-1.5 !text-xs disabled:opacity-50"
                            >
                                <FaSave />
                                {saving ? '...' : 'Simpan'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}
        </>
    );
};

export default MenuManage;
